import { action, makeAutoObservable, observable } from "mobx"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import cartService from "@services/cart/cartService"
import { CartDetailModel } from "@models/cart/cartDetailModel"
import type { IAssignUnitImportPreviewOutput } from "@models/cart/assignUnitImportPreviewModel"

class CartStore {
  @observable isLoading!: boolean
  @observable carts!: PagedResultDto<any>
  @observable cartDetail!: CartDetailModel
  @observable cartConfig!: any
  @observable productsInCard!: any
  @observable salesPolicyInCart!: any
  @observable listBrokerage!: any
  @observable agenciesMaster!: PagedResultDto<any>
  @observable auditLogData!: any
  @observable cartOverview!: any
  @observable productInCardOverview!: any
  @observable listUserByAgencyId!: PagedResultDto<any>
  @observable importPreview!: IAssignUnitImportPreviewOutput | null

  constructor() {
    makeAutoObservable(this)
    this.carts = { items: [], totalCount: 0 }
    this.productsInCard = { items: [], totalCount: 0 }
    this.salesPolicyInCart = []
    this.listBrokerage = { items: [], totalCount: 0 }
    this.agenciesMaster = { items: [], totalCount: 0 }
    this.auditLogData = []
    this.cartOverview = {}
    this.productInCardOverview = {}
    this.importPreview = null
  }

  @action
  async create(body: any) {
    this.isLoading = true
    const result = await cartService
      .create(body)
      .finally(() => (this.isLoading = false))
    this.cartDetail = result
  }

  @action
  async update(body: any) {
    this.isLoading = true
    await cartService
      .update(body)
      .then((data) => ((this.cartDetail = data), (this.isLoading = false)))
  }

  @action
  async updateConfiguration(body: any) {
    this.isLoading = true
    await cartService
      .updateConfiguration(body)
      .finally(() => (this.isLoading = false))
  }
  @action
  async initDetail() {
    this.cartDetail = new CartDetailModel()
  }

  @action
  async get(id: number) {
    this.isLoading = true
    const result = await cartService
      .get(id)
      .finally(() => (this.isLoading = false))
    this.cartDetail = result
  }

  @action
  async getCartConfig(id: number) {
    this.isLoading = true
    const result = await cartService
      .getCartConfig(id)
      .finally(() => (this.isLoading = false))
    this.cartConfig = result
  }

  async getOverview(id: number) {
    const result = await cartService.getOverview(id)
    this.cartOverview = result
  }

  async getOverviewProductInCard(id: number) {
    const result = await cartService.getOverviewProductInCard(id)
    this.productInCardOverview = result
  }

  @action
  async getAll(params: any) {
    this.isLoading = true
    const result = await cartService
      .getAll(params)
      .finally(() => (this.isLoading = false))
    this.carts = result
    return result
  }

  @action
  async getAllProductInCard(params: any) {
    this.isLoading = true
    const result = await cartService
      .getAllProductInCard(params)
      .finally(() => (this.isLoading = false))
    this.productsInCard = result
    return result
  }
  @action
  async getSalesPolicy(params: any) {
    this.isLoading = true
    const result = await cartService
      .getSalesPolicy(params)
      .finally(() => (this.isLoading = false))
    this.salesPolicyInCart = result
    return result
  }

  @action
  async assignSalesPolicy(body: any) {
    this.isLoading = true
    await cartService
      .assignSalesPolicy(body)
      .finally(() => (this.isLoading = false))
  }

  @action
  async getAlllistBrokerage(params: any) {
    this.isLoading = true
    const result = await cartService
      .getAlllistBrokerage(params)
      .finally(() => (this.isLoading = false))
    this.listBrokerage = result
    return result
  }

  @action
  async deActive(id: number, isActive: boolean) {
    this.isLoading = true
    await cartService.deActive(id, isActive).finally(() => (this.isLoading = false))
  }

  @action
  async updateActive(id: number) {
    this.isLoading = true
    await cartService.updateActive(id).finally(() => (this.isLoading = false))
  }

  @action
  async getAllAgencyMasterByCart(params: any) {
    this.isLoading = true
    const result = await cartService
      .getAllAgencyMasterByCart(params)
      .finally(() => (this.isLoading = false))
    this.agenciesMaster = result
    return result
  }

  @action
  async assignAgencyMaster(body: any) {
    this.isLoading = true
    const result = await cartService
      .assignAgencyMaster(body)
      .finally(() => (this.isLoading = false))
    this.cartDetail = result
  }

  @action
  async assignProducts(body: any) {
    this.isLoading = true
    await cartService
      .assignProducts(body)
      .finally(() => (this.isLoading = false))
  }
  @action
  async getAuditLog(id: any) {
    const result = await cartService.getAuditLog(id)

    this.auditLogData = result
  }

  @action
  async exportCart(params: any, payload: any) {
    this.isLoading = true
    await cartService
      .exportCart(params, payload)
      .finally(() => (this.isLoading = false))
  }

  /** Export giỏ hàng bản mới — `level` 1 = cơ bản, 2 = chi tiết (Feature-0202). */
  @action
  async exportCartV1(params: any, level: number) {
    this.isLoading = true
    await cartService
      .exportCartV1(params, level)
      .finally(() => (this.isLoading = false))
  }

  @action
  async exportProductInCart(params: any, payload: any) {
    this.isLoading = true
    await cartService
      .exportProductInCart(params, payload)
      .finally(() => (this.isLoading = false))
  }

  @action
  async removeUnit(body) {
    this.isLoading = true
    await cartService.removeUnit(body).finally(() => (this.isLoading = false))
  }

  @action
  async getUserByAgencyId(params: any) {
    this.isLoading = true
    const result = await cartService
      .getUserByAgencyId(params)
      .finally(() => (this.isLoading = false))
    this.listUserByAgencyId = result
    return result
  }

  @action
  async previewAssignUnitImport(cartId: number, file: File) {
    this.isLoading = true
    const result = await cartService
      .previewAssignUnitImport(cartId, file)
      .finally(() => (this.isLoading = false))
    this.importPreview = result
    return result
  }

  @action
  clearImportPreview() {
    this.importPreview = null
  }

  @action
  async assignUnitToCart(body: any) {
    this.isLoading = true
    await cartService
      .AssignUnitToCart(body)
      .finally(() => (this.isLoading = false))
  }

  @action
  async updateCartProductViewStatus(body: any) {
    this.isLoading = true
    const result = await cartService.updateCartProductViewStatus(body).finally(() => (this.isLoading = false))
    return result
  }
}

export default CartStore
